import { useTranslation } from 'react-i18next';
import { fromLogPosition, shutterLabel, SLIDER_STEPS, toLogPosition } from '../../utils/camera/controls';

/*
 * The adjustments of a live camera, the same in every camera of the app
 * (Field Mode's, the dish station's, the microscope camera, the QR scanner),
 * for the live picture before the shot:
 *
 *   Focus   the focus meter (useFocusMeter): a bar of the picture's sharpness
 *           against the sharpest it has been, and Sharp / Nearly / Blurry;
 *           where the camera offers it, Automatic or Manual focus with a
 *           distance slider, and "tap the picture to focus there"; where it
 *           offers neither, a note that it focuses by itself.
 *   Light   the exposure, on the camera (EV) where it offers it, else a
 *           digital brightness that is applied to the picture too and says
 *           so (not where `digital` is false: the QR scanner decodes the
 *           camera's own frames); the phone's light where it has one.
 *
 * and the Pro drawer's (2026-10-22; docs/FIELD_AI_VISION_SPEC.md 10.1), each
 * only where this camera offers it: the focus Locked where it focused; the
 * exposure Locked (AE-L) with its shutter and ISO set by hand, on sliders
 * even in stops; the white balance by hand in kelvin; the zoom (a pinch on
 * the picture sets it too); and the aperture, which no browser sets: Fixed.
 * Not where `pro` is false (the QR scanner: focus and light only).
 *
 * `controls` is useCameraControls()'s answer, `meter`/`onResetMeter`
 * useFocusMeter()'s. `tone` is 'dark' over a viewfinder, 'light' on a page.
 * `onReset` hears "Back to automatic" too: the host sets its shot back to a
 * single photo (useCaptureMode), so nothing is left armed by hand.
 */
export const LEVEL_COLOUR = { sharp: 'var(--c-success-fg)', close: 'var(--c-warning-text-fg)', blurry: 'var(--c-danger-fg)',
                              waiting: 'var(--c-text-faint)' };

const fmt = (v, digits = 1) => (Number.isFinite(v) ? (v > 0 ? `+${v.toFixed(digits)}` : v.toFixed(digits)) : '–');

export function FocusBar({ meter, tone = 'light', testId = 'focus-meter' }) {
  const { t } = useTranslation('camera');
  const pct = meter.ratio === null ? 0 : Math.round(meter.ratio * 100);
  const levelText = t(`focus.${meter.level}`);
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <div role="meter" aria-label={t('focus.label')} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}
           aria-valuetext={meter.ratio === null ? levelText : `${pct}% · ${levelText}`}
           data-testid={testId} data-level={meter.level} data-percent={pct}
           style={{ flex: 1, height: 12, borderRadius: 6, overflow: 'hidden',
                    background: tone === 'dark' ? 'rgba(255,255,255,0.25)' : 'var(--c-border)' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: LEVEL_COLOUR[meter.level], transition: 'width 150ms linear' }} />
      </div>
      <strong data-testid={`${testId}-level`} style={{ fontSize: 13, minWidth: 64,
                                                       color: tone === 'dark' ? '#fff' : LEVEL_COLOUR[meter.level] }}>
        {levelText}
      </strong>
    </div>
  );
}

export default function CameraControls({ controls, meter = null, onResetMeter = null, onReset = null, tone = 'light',
                                         digital = true, pro = true, testIdPrefix = 'camera' }) {
  const { t } = useTranslation('camera');
  const caps = controls.caps && !pro
    ? { ...controls.caps, focusLock: null, exposureLock: false, whiteBalance: null, zoom: null }
    : controls.caps;
  if (!caps) return null;
  const id = name => `${testIdPrefix}-${name}`;
  const dark = tone === 'dark';
  const muted = { margin: 0, fontSize: 12, color: dark ? 'rgba(255,255,255,0.8)' : 'var(--c-text-muted-fg)' };
  const field = { display: 'flex', flexDirection: 'column', gap: 2, fontSize: 13 };
  const btn = active => ({
    minHeight: 32, padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: active ? 700 : 400,
    border: `1px solid ${active ? 'var(--c-primary)' : (dark ? 'rgba(255,255,255,0.5)' : 'var(--c-border-strong)')}`,
    background: active ? (dark ? 'rgba(255,255,255,0.25)' : 'var(--c-primary-soft)') : 'transparent',
    color: dark ? '#fff' : 'var(--c-text-fg)',
  });
  const { exposure } = caps;
  const value = controls.exposure;
  const slider = exposure.kind === 'camera' || digital;
  const focusMode = controls.focus.locked ? 'locked' : (controls.focus.manual ? 'manual' : 'auto');
  const manual = controls.manualExposure;
  const wb = controls.whiteBalance || {};
  return (
    <section aria-label={t('title')} data-testid={id('controls')} data-exposure={exposure.kind}
             style={{ display: 'flex', flexDirection: 'column', gap: 8, color: dark ? '#fff' : 'var(--c-text-fg)' }}>
      <fieldset style={groupStyle(dark)} data-testid={id('focus')}>
        <legend style={legendStyle}>{t('focus.title')}</legend>
        {meter && <FocusBar meter={meter} tone={tone} testId={id('focus-meter')} />}
        {caps.manualFocus || caps.focusLock ? (
          <>
            <div role="radiogroup" aria-label={t('focus.mode')} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button type="button" role="radio" aria-checked={focusMode === 'auto'} data-testid={id('focus-auto')}
                      onClick={() => (controls.focus.locked ? controls.setFocusLock(false) : controls.setManualFocus(false))}
                      style={btn(focusMode === 'auto')}>
                {t('focus.auto')}
              </button>
              {caps.focusLock && (
                <button type="button" role="radio" aria-checked={focusMode === 'locked'} data-testid={id('focus-lock')}
                        onClick={() => controls.setFocusLock(true)} style={btn(focusMode === 'locked')}>
                  {t('focus.lock')}
                </button>
              )}
              {caps.manualFocus && (
                <button type="button" role="radio" aria-checked={focusMode === 'manual'} data-testid={id('focus-manual')}
                        onClick={() => controls.setManualFocus(true)} style={btn(focusMode === 'manual')}>
                  {t('focus.manual')}
                </button>
              )}
            </div>
            {focusMode === 'locked' && <p style={muted} data-testid={id('focus-locked')}>{t('focus.lockedHelp')}</p>}
            {focusMode === 'manual' && (
              <label style={field}>
                <span>{t('focus.distance')} <bdi dir="ltr">{fmt(controls.focus.distance, 2)}</bdi></span>
                <input type="range" min={caps.focusDistance.min} max={caps.focusDistance.max} step={caps.focusDistance.step}
                       value={controls.focus.distance ?? caps.focusDistance.min} data-testid={id('focus-distance')}
                       aria-label={t('focus.distance')} onChange={e => controls.setFocusDistance(Number(e.target.value))} />
              </label>
            )}
          </>
        ) : (
          <p style={muted}>{t(caps.pointOfInterest ? 'focus.autoOnly' : 'focus.byItself')}</p>
        )}
        {caps.pointOfInterest && <p style={muted} data-testid={id('focus-tap')}>{t('focus.tap')}</p>}
        {meter && <p style={muted}>{t('focus.help')}</p>}
        {meter && onResetMeter && (
          <div>
            <button type="button" onClick={onResetMeter} data-testid={id('focus-reset-best')} style={btn(false)}>
              {t('focus.resetBest')}
            </button>
          </div>
        )}
      </fieldset>
      <fieldset style={groupStyle(dark)} data-testid={id('light')}>
        <legend style={legendStyle}>{t('light.title')}</legend>
        {caps.exposureLock && (
          <div role="radiogroup" aria-label={t('light.mode')} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button type="button" role="radio" aria-checked={!manual} data-testid={id('exposure-auto')}
                    onClick={() => controls.setExposureLock(false)} style={btn(!manual)}>
              {t('light.auto')}
            </button>
            <button type="button" role="radio" aria-checked={!!manual} data-testid={id('exposure-lock')}
                    onClick={() => controls.setExposureLock(true)} style={btn(!!manual)}>
              {t('light.lock')}
            </button>
          </div>
        )}
        {manual ? (
          <>
            <label style={field}>
              <span>{t('light.shutter')} <bdi dir="ltr" data-testid={id('shutter-value')}>{shutterLabel(manual.exposureTime)}</bdi></span>
              <input type="range" min={0} max={SLIDER_STEPS} step={1} data-testid={id('shutter')}
                     value={toLogPosition(manual.exposureTime, caps.exposureTime)} aria-label={t('light.shutter')}
                     aria-valuetext={shutterLabel(manual.exposureTime)}
                     onChange={e => controls.setExposureTime(fromLogPosition(Number(e.target.value), caps.exposureTime))} />
            </label>
            {caps.iso && (
              <label style={field}>
                <span>{t('light.iso')} <bdi dir="ltr" data-testid={id('iso-value')}>{Math.round(manual.iso)}</bdi></span>
                <input type="range" min={0} max={SLIDER_STEPS} step={1} data-testid={id('iso')}
                       value={toLogPosition(manual.iso, caps.iso)} aria-label={t('light.iso')}
                       aria-valuetext={String(Math.round(manual.iso))}
                       onChange={e => controls.setIso(fromLogPosition(Number(e.target.value), caps.iso))} />
              </label>
            )}
            <p style={muted}>{t('light.lockHelp')}</p>
          </>
        ) : slider ? (
          <>
            <label style={field}>
              <span>
                {t(exposure.kind === 'camera' ? 'light.exposure' : 'light.digital')}{' '}
                <bdi dir="ltr" data-testid={id('exposure-value')}>{fmt(value)}</bdi>
              </span>
              <input type="range" min={exposure.min} max={exposure.max} step={exposure.step} value={value}
                     data-testid={id('exposure')}
                     aria-label={t(exposure.kind === 'camera' ? 'light.exposure' : 'light.digital')}
                     onChange={e => controls.setExposure(Number(e.target.value))} />
            </label>
            <p style={muted}>{t(exposure.kind === 'camera' ? 'light.exposureHelp' : 'light.digitalHelp')}</p>
          </>
        ) : (
          <p style={muted} data-testid={id('exposure-none')}>{t('light.none')}</p>
        )}
        {caps.whiteBalance && (
          <>
            <div role="radiogroup" aria-label={t('light.whiteBalance')}
                 style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 13 }} aria-hidden="true">{t('light.whiteBalance')}</span>
              <button type="button" role="radio" aria-checked={!wb.manual} data-testid={id('wb-auto')}
                      onClick={() => controls.setManualWhiteBalance(false)} style={btn(!wb.manual)}>
                {t('light.auto')}
              </button>
              <button type="button" role="radio" aria-checked={!!wb.manual} data-testid={id('wb-manual')}
                      onClick={() => controls.setManualWhiteBalance(true)} style={btn(!!wb.manual)}>
                {t('light.wbManual')}
              </button>
            </div>
            {wb.manual && (
              <label style={field}>
                <span>
                  {t('light.temperature')}{' '}
                  <bdi dir="ltr" data-testid={id('wb-temperature-value')}>{`${Math.round(wb.temperature)} K`}</bdi>
                </span>
                <input type="range" min={caps.whiteBalance.temperature.min} max={caps.whiteBalance.temperature.max}
                       step={caps.whiteBalance.temperature.step} value={wb.temperature} data-testid={id('wb-temperature')}
                       aria-label={t('light.temperature')}
                       onChange={e => controls.setColorTemperature(Number(e.target.value))} />
              </label>
            )}
          </>
        )}
        {caps.torch && (
          <div>
            <button type="button" aria-pressed={controls.torch} data-testid={id('torch')}
                    onClick={() => controls.setTorch(!controls.torch)} style={btn(controls.torch)}>
              {t(controls.torch ? 'light.torchOff' : 'light.torchOn')}
            </button>
          </div>
        )}
      </fieldset>
      {pro && (
        <fieldset style={groupStyle(dark)} data-testid={id('lens')}>
          <legend style={legendStyle}>{t('lens.title')}</legend>
          {caps.zoom && (
            <>
              <label style={field}>
                <span>
                  {t('lens.zoom')}{' '}
                  <bdi dir="ltr" data-testid={id('zoom-value')}>{`${(controls.zoom ?? caps.zoom.value).toFixed(1)}×`}</bdi>
                </span>
                <input type="range" min={caps.zoom.min} max={caps.zoom.max} step={caps.zoom.step}
                       value={controls.zoom ?? caps.zoom.value} data-testid={id('zoom')} aria-label={t('lens.zoom')}
                       onChange={e => controls.setZoom(Number(e.target.value))} />
              </label>
              <p style={muted}>{t('lens.pinch')}</p>
            </>
          )}
          <p style={muted} data-testid={id('aperture')}>{t('lens.aperture')}</p>
        </fieldset>
      )}
      {controls.refused && (
        <p role="alert" data-testid={id('refused')} data-what={controls.refused} style={{ ...muted, color: dark ? '#ffcdd2' : 'var(--c-danger-fg)' }}>
          {t('refused', { what: t(`what.${controls.refused}`) })}
        </p>
      )}
      <div>
        <button type="button" data-testid={id('reset')} onClick={() => { controls.reset(); onReset?.(); }}
                style={btn(false)}>{t('reset')}</button>
      </div>
    </section>
  );
}

const groupStyle = dark => ({ border: `1px solid ${dark ? 'rgba(255,255,255,0.3)' : 'var(--c-border)'}`, borderRadius: 8,
                              padding: '4px 10px 8px', margin: 0, display: 'flex', flexDirection: 'column', gap: 6,
                              minWidth: 0 });
const legendStyle = { fontSize: 13, fontWeight: 600, padding: '0 4px' };
